import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Plane, Hotel, Car, MapPin, Calendar, Users, Pause, Play, CalendarClock, XCircle, Download, Receipt, Shield } from "lucide-react";
import { format } from "date-fns";
import { useState, useEffect } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "@/App";
import { toast } from "sonner";

const fmtINR = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// iPad/iPhone Safari ITP blocks the Razorpay mandate iframe — match the
// detection used in BookingPage so customers retrying from My Bookings get
// the same redirect-mode fallback. Centralising this here keeps the modal
// self-contained (it's also opened from AdminPage where BookingPage's
// helpers aren't in scope).
const isIOSSafariLike = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "dd MMM yyyy");
  } catch {
    return iso;
  }
};

const cap = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

/**
 * Self-contained detail view for a single persisted booking. Renders ALL
 * fields directly from the booking object (returned by GET /api/bookings)
 * — does not depend on the wizard's in-memory React state. This is what
 * the customer sees when clicking a row from "My Bookings".
 */
export default function BookingDetailModal({ booking, open, onClose, onUpdate }) {
  const [emiBusy, setEmiBusy] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [mandateBusy, setMandateBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [payments, setPayments] = useState(null);

  // Fetch payment history whenever the modal opens for a real booking.
  useEffect(() => {
    if (!booking?.id || !open) { setPayments(null); return; }
    let cancelled = false;
    axios.get(`${API}/bookings/${booking.id}/payments`).then(res => {
      if (!cancelled) setPayments(res.data);
    }).catch(e => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Could not load payment history:", e?.message);
      }
    });
    return () => { cancelled = true; };
  }, [booking?.id, open]);

  // Lazy-load the Razorpay Checkout script (the modal can be opened from
  // AdminPage where the script isn't loaded by the parent). Resolves to true
  // when window.Razorpay is available; rejects on script load failure.
  const ensureRazorpayLoaded = () =>
    new Promise((resolve, reject) => {
      if (typeof window.Razorpay !== "undefined") return resolve(true);
      const existing = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      );
      if (existing) {
        existing.addEventListener("load", () => resolve(true));
        existing.addEventListener("error", () => reject(new Error("Razorpay script failed to load")));
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Razorpay script failed to load"));
      document.body.appendChild(script);
    });

  // Retry the EMI auto-debit mandate setup for a booking stuck in
  // `upfront_paid` state with no `razorpay_subscription_id`. Creates a fresh
  // subscription on Razorpay, opens Checkout for the customer to authorise
  // the mandate, then verifies the resulting subscription server-side.
  // On iPad/iPhone Safari we switch to redirect mode (Razorpay's hosted
  // page) because ITP blocks the modal iframe — see BookingPage Iteration 31.
  const handleMandateSetup = async () => {
    if (!booking?.id) return;
    setMandateBusy(true);
    try {
      await ensureRazorpayLoaded();
      const subRes = await axios.post(`${API}/payments/create-subscription`, {
        booking_id: booking.id,
      });
      const { key, subscription_id, customer_id, name, email, contact, theme } = subRes.data || {};
      if (!subscription_id) throw new Error("Razorpay subscription could not be created");

      // ── iOS Safari → REDIRECT MODE ─────────────────────────────────
      if (isIOSSafariLike()) {
        try {
          sessionStorage.setItem(
            "tvs_pending_mandate",
            JSON.stringify({ bookingId: booking.id, at: Date.now() }),
          );
        } catch {/* sessionStorage may be unavailable in private mode */}
        const redirectOpts = {
          key,
          subscription_id,
          customer_id,
          name: "Travelsate",
          description: `EMI auto-debit mandate · ${booking.destination_name || ""}`.trim(),
          recurring: 1,
          prefill: { name: name || "", email: email || "", contact: contact || "" },
          theme: theme || { color: "#D4AF37" },
          callback_url: `${BACKEND_URL}/api/payments/mandate-redirect-return`,
          redirect: true,
        };
        const rzp = new window.Razorpay(redirectOpts);
        rzp.open();
        // The page is now navigating away — leave the busy flag set so the
        // CTA stays in "Setting up…" state until the browser swaps documents.
        return;
      }

      await new Promise((resolve, reject) => {
        const opts = {
          key,
          subscription_id,
          customer_id,
          name: "Travelsate",
          description: `EMI auto-debit mandate · ${booking.destination_name || ""}`.trim(),
          recurring: 1,
          prefill: { name: name || "", email: email || "", contact: contact || "" },
          theme: theme || { color: "#D4AF37" },
          handler: async (resp) => {
            try {
              await axios.post(`${API}/payments/verify-subscription`, {
                booking_id: booking.id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_subscription_id: resp.razorpay_subscription_id,
                razorpay_signature: resp.razorpay_signature,
              });
              resolve();
            } catch (err) { reject(err); }
          },
          modal: {
            ondismiss: () => reject(new Error("Customer dismissed mandate authorisation")),
          },
        };
        const checkout = new window.Razorpay(opts);
        checkout.open();
      });

      toast.success("Auto-debit set up. Your EMI schedule is now active!");
      if (onUpdate) await onUpdate();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not set up auto-debit";
      toast.error(msg);
    } finally {
      setMandateBusy(false);
    }
  };

  if (!booking) return null;

  const {
    id,
    destination_name,
    package_type,
    start_date,
    end_date,
    days,
    adults = 0,
    children = 0,
    infants = 0,
    adult_names = [],
    adult_genders = [],
    child_names = [],
    child_genders = [],
    infant_names = [],
    infant_genders = [],
    infant_dobs = [],
    departure_city,
    departure_airport,
    arrival_airport,
    flight_costs = 0,
    hotel_costs = 0,
    transfer_costs = 0,
    rooms_needed,
    include_itinerary_cost,
    itinerary_cost = 0,
    itinerary_days_count,
    tax = 0,
    total_cost = 0,
    booking_cost = 0,
    payment_plan,
    payment_status,
    booking_schedule,
    created_at,
    promo_code = null,
    promo_discount_pct = 0,
    promo_discount_inr = 0,
    additional_requests = null,
  } = booking;

  const tripDays =
    days ||
    (start_date && end_date
      ? Math.ceil(
          (new Date(end_date) - new Date(start_date)) / (1000 * 60 * 60 * 24)
        )
      : 0);

  const isConfirmed = payment_status === "completed";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="glass-card border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="booking-detail-modal"
      >
        <DialogHeader>
          <div className="text-center mb-2">
            <div
              className={`w-14 h-14 rounded-full ${
                isConfirmed ? "bg-green-500/20" : "bg-amber-500/20"
              } flex items-center justify-center mx-auto mb-3`}
            >
              <Check
                className={`w-7 h-7 ${
                  isConfirmed ? "text-green-400" : "text-amber-400"
                }`}
              />
            </div>
            <DialogTitle className="font-serif text-2xl text-white">
              {isConfirmed ? "Booking Confirmed" : "Booking Pending"}
            </DialogTitle>
            <p className="text-white/50 text-sm mt-1">
              Reference:{" "}
              <span
                className="text-[#D4AF37] font-medium tracking-wider"
                data-testid="detail-booking-ref"
              >
                {id?.slice(0, 8).toUpperCase()}
              </span>
            </p>
            {created_at && (
              <p className="text-white/30 text-xs mt-0.5">
                Booked on {fmtDate(created_at)}
              </p>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6 px-1">
          {/* Booking Details */}
          <div>
            <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Booking Details
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Destination</span>
                <span className="text-white">{destination_name || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Package</span>
                <span className="text-[#D4AF37] font-medium">
                  {cap(package_type)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Travel Dates</span>
                <span className="text-white">
                  {fmtDate(start_date)} — {fmtDate(end_date)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Duration</span>
                <span className="text-white">{tripDays} night{tripDays === 1 ? "" : "s"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Traveling From</span>
                <span className="text-white">
                  {departure_city || "N/A"}
                  {departure_airport ? ` (${departure_airport})` : ""}
                </span>
              </div>
              {arrival_airport && (
                <div className="flex justify-between">
                  <span className="text-white/50">Arrival Airport</span>
                  <span className="text-white">{arrival_airport}</span>
                </div>
              )}
            </div>
          </div>

          {/* Traveler Details */}
          <div>
            <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2 flex items-center gap-2">
              <Users className="w-4 h-4" /> Traveler Details
            </h3>
            <div className="space-y-1.5 text-sm">
              {adult_names.length === 0 && children + infants === 0 && (
                <p className="text-white/30 text-xs italic">No traveler details on record.</p>
              )}
              {adult_names.map((n, i) => (
                <div key={`a-${i}`} className="flex justify-between">
                  <span className="text-white/50">Adult {i + 1}</span>
                  <span className="text-white">
                    {n}
                    {adult_genders[i] ? ` (${cap(adult_genders[i])})` : ""}
                  </span>
                </div>
              ))}
              {child_names.map((n, i) => (
                <div key={`c-${i}`} className="flex justify-between">
                  <span className="text-white/50">Child {i + 1}</span>
                  <span className="text-white">
                    {n}
                    {child_genders[i] ? ` (${cap(child_genders[i])})` : ""}
                  </span>
                </div>
              ))}
              {infant_names.map((n, i) => (
                <div key={`i-${i}`} className="flex justify-between">
                  <span className="text-white/50">Infant {i + 1}</span>
                  <span className="text-white">
                    {n}
                    {infant_genders[i] ? ` (${cap(infant_genders[i])})` : ""}
                    {infant_dobs[i] ? ` · DOB ${fmtDate(infant_dobs[i])}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Cost Breakdown */}
          <div>
            <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">
              Cost Breakdown
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50 flex items-center gap-1.5">
                  <Plane className="w-3.5 h-3.5" /> Flights ({adults}A + {children}C + {infants}I)
                </span>
                <span className="text-white">{fmtINR(flight_costs)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50 flex items-center gap-1.5">
                  <Hotel className="w-3.5 h-3.5" /> Hotel
                  {rooms_needed ? ` (${rooms_needed} room × ${tripDays} nights)` : ""}
                </span>
                <span className="text-white">{fmtINR(hotel_costs)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50 flex items-center gap-1.5">
                  <Car className="w-3.5 h-3.5" /> Transfers
                </span>
                <span className="text-white">{fmtINR(transfer_costs)}</span>
              </div>
              {include_itinerary_cost && itinerary_cost > 0 && (
                <div className="flex justify-between" data-testid="detail-itinerary-line">
                  <span className="text-white/50">
                    🗺️ Itinerary
                    {itinerary_days_count ? ` (${itinerary_days_count}d × pax)` : ""}
                  </span>
                  <span className="text-white">{fmtINR(itinerary_cost)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-white/50">Tax (18%)</span>
                <span className="text-white">{fmtINR(tax)}</span>
              </div>
              {promo_code && promo_discount_inr > 0 && (
                <div
                  className="flex justify-between"
                  data-testid="detail-promo-discount-row"
                >
                  <span className="text-emerald-400/90">
                    Promo <span className="font-mono text-emerald-300">{promo_code}</span> ({promo_discount_pct}% off)
                  </span>
                  <span className="text-emerald-400">−{fmtINR(promo_discount_inr)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-white/10">
                <span className="text-white font-medium">Total Cost</span>
                <span
                  className="text-[#D4AF37] text-lg font-medium"
                  data-testid="detail-total-cost"
                >
                  {fmtINR(total_cost)}
                </span>
              </div>
            </div>
          </div>

          {/* Customer special requests + guided-day selection (only render if either is set) */}
          {(additional_requests || itinerary_days_count > 0) && (
            <div data-testid="detail-customer-notes">
              <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">
                Trip Customisation
              </h3>
              <div className="space-y-2 text-sm">
                {itinerary_days_count > 0 && (
                  <div className="flex justify-between">
                    <span className="text-white/50">Guided itinerary days</span>
                    <span className="text-white" data-testid="detail-itinerary-days">{itinerary_days_count}</span>
                  </div>
                )}
                {additional_requests && (
                  <div>
                    <p className="text-white/50 mb-1">Special requests</p>
                    <p
                      className="text-white/90 bg-white/[0.03] border border-white/5 rounded-md px-3 py-2 whitespace-pre-wrap text-[13px] leading-relaxed"
                      data-testid="detail-additional-requests"
                    >
                      {additional_requests}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment */}
          <div>
            <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">
              Payment Details
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Amount Paid</span>
                <span className="text-white">
                  {fmtINR(payment_plan === "full" ? total_cost : booking_cost)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Payment Plan</span>
                <span className="text-white">
                  {payment_plan === "full"
                    ? "Full Payment"
                    : payment_plan
                    ? `${cap(payment_plan)} Installments`
                    : "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Status</span>
                <span
                  className={`font-medium ${
                    isConfirmed ? "text-green-400" : "text-amber-400"
                  }`}
                  data-testid="detail-payment-status"
                >
                  {cap(payment_status) || "Pending"}
                </span>
              </div>
            </div>
          </div>

          {/* Booking Schedule (installments only) */}
          {booking_schedule && (
            <div data-testid="detail-booking-schedule">
              <h3 className="text-[#D4AF37] text-sm font-medium mb-2 border-b border-white/10 pb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Booking Schedule
              </h3>
              <p className="text-white/50 text-xs mb-3 leading-relaxed">
                As your installments come through, each component will be booked in this order:
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">✈ Flights booked by</span>
                  <span className="text-white">{fmtDate(booking_schedule.flights_booking_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">🏨 Hotel booked by</span>
                  <span className="text-white">{fmtDate(booking_schedule.hotel_booking_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">🚗 Transfers confirmed by</span>
                  <span className="text-white">{fmtDate(booking_schedule.transfers_booking_date)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Installment Plan — per-EMI breakdown (if any) */}
          {booking_schedule?.installments?.length > 0 && (
            <div data-testid="detail-installment-plan">
              <h3 className="text-[#D4AF37] text-sm font-medium mb-2 border-b border-white/10 pb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Installment Plan
              </h3>
              <p className="text-white/50 text-xs mb-3 leading-relaxed">
                {booking_schedule.n_installments} {booking_schedule.payment_plan} installments
                {booking_schedule.per_emi_inr ? ` of approx ₹${Math.round(booking_schedule.per_emi_inr).toLocaleString("en-IN")} each` : ""}.
                The first installment was paid at checkout — the rest are auto-debited and complete before travel (15 days prior for domestic, 30 days for international) so we can lock in flights and hotels.
              </p>
              <div className="space-y-1.5">
                {booking_schedule.installments.map((emi) => {
                  const due = new Date(emi.due_on);
                  const isPast = !Number.isNaN(due.getTime()) && due < new Date();
                  return (
                    <div
                      key={`emi-${emi.installment_no}`}
                      className="flex items-center justify-between text-xs bg-white/[0.03] border border-white/5 rounded px-3 py-2"
                      data-testid={`detail-installment-${emi.installment_no}`}
                    >
                      <span className="text-white/60">
                        <span className="text-[#D4AF37] mr-1.5">#{emi.installment_no}</span>
                        {fmtDate(emi.due_on)}
                        {isPast && emi.installment_no === 1 && (
                          <span className="ml-2 text-green-400/80 text-[10px]">paid</span>
                        )}
                      </span>
                      <span className="text-white">
                        ₹{Math.round(emi.amount_inr).toLocaleString("en-IN")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Auto-debit mandate not yet set up — surface a recoverable CTA
              so customers stuck in `upfront_paid` can finish the mandate
              from My Bookings without re-booking. */}
          {booking.payment_plan && booking.payment_plan !== "full" &&
            booking.payment_status === "upfront_paid" &&
            !booking.razorpay_subscription_id && (
            <div data-testid="mandate-setup-required">
              <h3 className="text-amber-300 text-sm font-medium mb-2 border-b border-amber-400/20 pb-2 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Auto-debit Setup Required
              </h3>
              <div className="bg-amber-500/10 border border-amber-400/30 rounded p-3.5">
                <p className="text-amber-100/90 text-xs leading-relaxed mb-3">
                  Your 1% upfront payment is captured, but the EMI auto-debit mandate hasn't been authorised yet.
                  Until you complete this one-time step, Razorpay can't debit your scheduled installments.
                </p>
                <button
                  data-testid="mandate-setup-button"
                  disabled={mandateBusy}
                  onClick={handleMandateSetup}
                  className="inline-flex items-center gap-1.5 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-black text-xs font-medium px-4 py-2 rounded transition-colors"
                >
                  {mandateBusy ? "Opening Razorpay…" : (
                    <>
                      <Shield className="w-3 h-3" /> Set up auto-debit now
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* EMI Mandate Controls — only when this booking has an active Razorpay subscription */}
          {booking.razorpay_subscription_id && (
            <div data-testid="emi-mandate-controls">
              <h3 className="text-[#D4AF37] text-sm font-medium mb-2 border-b border-white/10 pb-2 flex items-center gap-2">
                <Pause className="w-4 h-4" /> EMI Auto-debit Mandate
              </h3>
              <div className="flex items-center justify-between gap-3 bg-white/[0.03] border border-white/5 rounded px-3 py-3">
                <div className="text-xs">
                  <div className="text-white/80 mb-0.5">
                    Status:{" "}
                    <span
                      className={
                        booking.subscription_status === "paused"
                          ? "text-amber-400"
                          : booking.subscription_status === "halted"
                          ? "text-red-400"
                          : booking.subscription_status === "completed"
                          ? "text-emerald-400"
                          : "text-[#D4AF37]"
                      }
                      data-testid="emi-status"
                    >
                      {cap(booking.subscription_status || "active")}
                    </span>
                  </div>
                  <div className="text-white/40 leading-relaxed">
                    {booking.subscription_status === "paused"
                      ? "Auto-debits are paused. Tap Resume when you're ready — the remaining installments will continue on the same schedule from then on."
                      : `Auto-debits run on schedule. You can pause anytime — your booking stays confirmed and only the debit cadence shifts.`}
                  </div>
                </div>
                {booking.subscription_status === "paused" ? (
                  <button
                    data-testid="emi-resume-button"
                    disabled={emiBusy}
                    onClick={async () => {
                      setEmiBusy(true);
                      try {
                        await axios.post(`${API}/bookings/${booking.id}/resume-emi`);
                        toast.success("EMI auto-debit resumed");
                        if (onUpdate) await onUpdate();
                      } catch (e) {
                        toast.error(
                          e.response?.data?.detail || "Couldn't resume EMI"
                        );
                      } finally {
                        setEmiBusy(false);
                      }
                    }}
                    className="shrink-0 inline-flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#E5C468] text-black text-xs font-medium px-3 py-2 rounded transition-colors disabled:opacity-50"
                  >
                    <Play className="w-3 h-3" /> Resume EMI
                  </button>
                ) : booking.subscription_status === "halted" ||
                  booking.subscription_status === "completed" ||
                  booking.subscription_status === "cancelled" ? null : (
                  <button
                    data-testid="emi-pause-button"
                    disabled={emiBusy}
                    onClick={async () => {
                      setEmiBusy(true);
                      try {
                        await axios.post(`${API}/bookings/${booking.id}/pause-emi`);
                        toast.success("EMI auto-debit paused");
                        if (onUpdate) await onUpdate();
                      } catch (e) {
                        toast.error(
                          e.response?.data?.detail || "Couldn't pause EMI"
                        );
                      } finally {
                        setEmiBusy(false);
                      }
                    }}
                    className="shrink-0 inline-flex items-center gap-1.5 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white/80 text-xs font-medium px-3 py-2 rounded transition-colors disabled:opacity-50"
                  >
                    <Pause className="w-3 h-3" /> Pause EMI
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Payment History + Invoice Download */}
          {payments && (
            <div data-testid="payment-history-section">
              <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-2">
                <h3 className="text-[#D4AF37] text-sm font-medium flex items-center gap-2">
                  <Receipt className="w-4 h-4" /> Payment History
                </h3>
                <a
                  href={`${API}/bookings/${booking.id}/invoice.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline"
                  data-testid="download-invoice-button"
                >
                  <Download className="w-3 h-3" /> Download invoice (PDF)
                </a>
              </div>
              <div className="flex items-center justify-between text-xs mb-3 bg-white/[0.03] border border-white/5 rounded px-3 py-2">
                <span className="text-white/60">
                  Paid <span className="text-[#D4AF37] font-medium">₹{Math.round(payments.amount_paid_inr).toLocaleString("en-IN")}</span> of ₹{Math.round(payments.total_cost_inr).toLocaleString("en-IN")}
                </span>
                <span className="text-white/40">
                  Remaining ₹{Math.round(payments.amount_remaining_inr).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {payments.history.length === 0 && (
                  <p className="text-white/30 text-xs">No payments recorded yet.</p>
                )}
                {payments.history.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-xs border border-white/5 rounded px-2.5 py-1.5"
                    data-testid={`payment-row-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-white/80 truncate">{h.label}</div>
                      <div className="text-white/40 text-[10px]">{fmtDate(h.due_on)}</div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="text-white">₹{Math.round(h.amount_inr).toLocaleString("en-IN")}</div>
                      <div
                        className={`text-[10px] ${
                          h.status === "paid" ? "text-emerald-400"
                          : h.status === "cancelled" ? "text-red-400"
                          : "text-amber-400"
                        }`}
                      >
                        {h.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manage Booking — Cancel & Reschedule */}
          {booking.payment_status !== "cancelled" && (
            <div data-testid="manage-booking-controls">
              <h3 className="text-[#D4AF37] text-sm font-medium mb-2 border-b border-white/10 pb-2 flex items-center gap-2">
                <CalendarClock className="w-4 h-4" /> Manage Booking
              </h3>

              {/* Reschedule form */}
              {rescheduling ? (
                <div className="bg-white/[0.03] border border-white/5 rounded p-3 mb-2" data-testid="reschedule-form">
                  <p className="text-white/60 text-xs mb-3 leading-relaxed">
                    Pick a new travel window. A ₹2,500 reschedule fee applies; any flight/hotel
                    fare difference will be quoted by ops after you submit.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="text-white/50 text-[10px] uppercase tracking-wider block mb-1">New start</label>
                      <input
                        type="date"
                        value={newStart}
                        onChange={(e) => setNewStart(e.target.value)}
                        data-testid="reschedule-new-start"
                        className="w-full bg-black/40 border border-white/10 rounded text-white text-xs px-2 py-1.5 focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-[10px] uppercase tracking-wider block mb-1">New end</label>
                      <input
                        type="date"
                        value={newEnd}
                        onChange={(e) => setNewEnd(e.target.value)}
                        data-testid="reschedule-new-end"
                        className="w-full bg-black/40 border border-white/10 rounded text-white text-xs px-2 py-1.5 focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setRescheduling(false);
                        setNewStart("");
                        setNewEnd("");
                      }}
                      disabled={lifecycleBusy}
                      className="text-xs text-white/60 hover:text-white px-3 py-1.5"
                      data-testid="reschedule-cancel-button"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      disabled={lifecycleBusy || !newStart || !newEnd}
                      onClick={async () => {
                        setLifecycleBusy(true);
                        try {
                          const res = await axios.post(`${API}/bookings/${booking.id}/reschedule`, {
                            new_start_date: newStart,
                            new_end_date: newEnd,
                          });
                          toast.success(
                            `Reschedule recorded. Ops will confirm any fare difference within 24h. Reschedule fee: ₹${res.data.reschedule_fee_inr.toLocaleString("en-IN")}.`
                          );
                          setRescheduling(false);
                          setNewStart("");
                          setNewEnd("");
                          if (onUpdate) await onUpdate();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Couldn't reschedule");
                        } finally {
                          setLifecycleBusy(false);
                        }
                      }}
                      className="text-xs bg-[#D4AF37] text-black font-medium px-3 py-1.5 rounded hover:bg-[#E5C468] disabled:opacity-50"
                      data-testid="reschedule-submit-button"
                    >
                      Submit reschedule
                    </button>
                  </div>
                </div>
              ) : confirmCancel ? (
                /* Cancel confirmation */
                <div className="bg-red-950/30 border border-red-500/20 rounded p-3 mb-2" data-testid="cancel-confirm">
                  <p className="text-red-200 text-xs leading-relaxed mb-3">
                    Cancelling is final. You&apos;ll get a travel credit per our policy (75% if 30+ days out,
                    40% if 15–30 days, none under 15 days). The 1% booking fee is non-refundable.
                  </p>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setConfirmCancel(false)}
                      disabled={lifecycleBusy}
                      className="text-xs text-white/60 hover:text-white px-3 py-1.5"
                      data-testid="cancel-abort-button"
                    >
                      Keep booking
                    </button>
                    <button
                      type="button"
                      disabled={lifecycleBusy}
                      onClick={async () => {
                        setLifecycleBusy(true);
                        try {
                          const res = await axios.post(`${API}/bookings/${booking.id}/cancel`, {});
                          toast.success(
                            `Cancelled. Travel credit: ₹${res.data.credit.travel_credit_inr.toLocaleString("en-IN")} (valid 6 months).`
                          );
                          setConfirmCancel(false);
                          if (onUpdate) await onUpdate();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Couldn't cancel");
                        } finally {
                          setLifecycleBusy(false);
                        }
                      }}
                      className="text-xs bg-red-600 hover:bg-red-500 text-white font-medium px-3 py-1.5 rounded disabled:opacity-50"
                      data-testid="cancel-confirm-button"
                    >
                      Yes, cancel my booking
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setRescheduling(true)}
                    className="inline-flex items-center gap-1.5 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white/80 text-xs font-medium px-3 py-2 rounded transition-colors"
                    data-testid="open-reschedule-button"
                  >
                    <CalendarClock className="w-3 h-3" /> Reschedule trip
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(true)}
                    className="inline-flex items-center gap-1.5 border border-red-500/30 hover:border-red-500 hover:text-red-300 text-red-300/80 text-xs font-medium px-3 py-2 rounded transition-colors"
                    data-testid="open-cancel-button"
                  >
                    <XCircle className="w-3 h-3" /> Cancel booking
                  </button>
                </div>
              )}
            </div>
          )}

          {booking.payment_status === "cancelled" && booking.cancellation_credit && (
            <div className="bg-white/[0.03] border border-white/5 rounded p-3" data-testid="cancellation-summary">
              <h3 className="text-red-300 text-sm font-medium mb-2">Cancelled</h3>
              <div className="text-xs text-white/70 space-y-1">
                <div className="flex justify-between">
                  <span className="text-white/50">Travel credit ({booking.cancellation_credit.credit_pct}%)</span>
                  <span className="text-[#D4AF37]">₹{booking.cancellation_credit.travel_credit_inr?.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Booking fee (non-refundable)</span>
                  <span className="text-white/60">₹{booking.cancellation_credit.booking_fee_forfeited_inr?.toLocaleString("en-IN")}</span>
                </div>
                <p className="text-white/40 text-[11px] pt-1">Credit valid for {booking.cancellation_credit.credit_expiry_months} months. Reply to the cancellation email when ready to use it.</p>
              </div>
            </div>
          )}

          {/* Cancellation Policy */}
          <div>
            <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">
              Cancellation & Reschedule Policy
            </h3>
            <ul className="space-y-1.5 text-xs text-white/40">
              <li>Bookings once confirmed are <span className="text-white/60">non-refundable</span>. 1% booking fee is non-refundable.</li>
              <li>30+ days before travel: Travel credit of 75% (excl. booking fee), valid 6 months.</li>
              <li>15-30 days before travel: Travel credit of 40%.</li>
              <li>Within 15 days: No refund or credit.</li>
              <li>One reschedule allowed 30+ days before travel (₹2,500 fee + fare difference).</li>
              <li>No-shows or missed installments: Forfeiture of all amounts paid.</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
